import { useEffect, useRef, useState, type RefObject } from 'react';
import { uploadExtension } from '../api';

/** What is being dragged over the Library: how many files, and the types the browser lets us see before the drop. */
export type LibraryDragPreview = { count: number; labels: string[] };

const carriesFiles = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

function preview(event: DragEvent): LibraryDragPreview {
  const items = Array.from(event.dataTransfer?.items ?? []).filter((item) => item.kind === 'file');
  return {
    // Some browsers list no items until the drop; the drag still holds at least one file.
    count: Math.max(1, items.length),
    labels: items.map((item) => uploadExtension(item.type)?.toUpperCase() ?? ''),
  };
}

/**
 * The whole Library page takes files: dragging them anywhere over it opens the drop target, dropping uploads them,
 * and leaving the page or pressing Esc puts everything back. Listens on the page's scroll region, so the empty space
 * under a short list counts too.
 */
export function useLibraryDrop(
  anchor: RefObject<HTMLElement | null>,
  enabled: boolean,
  onFiles: (files: File[]) => void,
): LibraryDragPreview | null {
  const [dragging, setDragging] = useState<LibraryDragPreview | null>(null);
  const take = useRef(onFiles);
  take.current = onFiles;
  useEffect(() => {
    const element = anchor.current;
    const target = element?.closest<HTMLElement>('.ws-page-scroll') ?? element;
    if (!enabled || !target) return;
    let depth = 0;
    const reset = () => {
      depth = 0;
      setDragging(null);
    };
    const enter = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      depth += 1;
      setDragging((current) => current ?? preview(event));
    };
    const over = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };
    const leave = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth -= 1;
      if (depth <= 0) reset();
    };
    const drop = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      reset();
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length) take.current(files);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') reset();
    };
    // Files let go anywhere else put the target away too (`useStrayDropGuard` keeps the browser from opening them).
    const elsewhere = (event: DragEvent) => {
      if (carriesFiles(event)) reset();
    };
    target.addEventListener('dragenter', enter);
    target.addEventListener('dragover', over);
    target.addEventListener('dragleave', leave);
    target.addEventListener('drop', drop);
    window.addEventListener('keydown', escape);
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', elsewhere);
    return () => {
      target.removeEventListener('dragenter', enter);
      target.removeEventListener('dragover', over);
      target.removeEventListener('dragleave', leave);
      target.removeEventListener('drop', drop);
      window.removeEventListener('keydown', escape);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', elsewhere);
      setDragging(null);
    };
  }, [anchor, enabled]);
  return dragging;
}

/**
 * Files let go where nothing takes them (the sidebar, the top bar, a canvas, a kind of the Library that does not take
 * files) do nothing, rather than the browser opening the file in place of Chat and ending every upload on its way.
 */
export function useStrayDropGuard(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const stray = (event: DragEvent) => {
      if (!carriesFiles(event) || event.defaultPrevented) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', stray);
    window.addEventListener('drop', stray);
    return () => {
      window.removeEventListener('dragover', stray);
      window.removeEventListener('drop', stray);
    };
  }, [enabled]);
}
